import { Dialog } from '@ordr-as/umami-react/dialog';
import { FC, useCallback, useMemo, useState } from 'react';
import useIntl from '../../hooks/useIntl';
import { useCartStore } from '../../context/cart/CartStore';
import { useAllProducts } from '../../hooks/useAllProducts';
import { useLocation } from '../../context/LocationContext';
import { BundleMatch } from '../../utils/bundleDetection';
import { buildAddComponentDto } from '../../utils/cart';
import { MenuItemVariantDto } from '../../../generated-api';
import {
  AddComponentDto,
  AddItemsDto,
  CartAdditionOption,
  CartOrderLine,
  CartOrderLineComponent,
} from '../../../types/cart';
import { ProductType } from '../../../types/location';

function mapCartComponentToAddComponent(component: CartOrderLineComponent): AddComponentDto {
  return {
    itemVariantId: component.itemVariantId,
    name: component.name,
    price: component.price,
    takeawayPrice: component.takeawayPrice,
    options: component.options.map((opt) => ({
      itemVariantId: opt.itemVariantId,
      name: opt.name,
      price: opt.price,
      takeawayPrice: opt.takeawayPrice,
      type: opt.type,
      quantity: opt.quantity,
    })),
    comment: component.comment,
    commentId: component.commentId,
    courseId: component.courseId,
    unitQuantity: component.unitQuantity,
    components: component.components?.map(mapCartComponentToAddComponent),
  };
}

/**
 * Builds the DeleteItemsDto[] and AddItemsDto needed to convert
 * individual cart lines into a composite bundle.
 */
function buildConversionPayload(match: BundleMatch, orderLines: CartOrderLine[], allProducts: MenuItemVariantDto[]) {
  const bundleProduct = allProducts.find((p) => p.variantLinkId === match.bundle.variantId);
  if (!bundleProduct || !bundleProduct.components) return undefined;

  // Build delete items: for each component, find matching cart lines
  const deleteItems: AddItemsDto[] = [];
  const usedLineQuantities = new Map<string, number>();

  for (const component of match.bundle.components) {
    let remainingQty = component.quantity * match.instances;

    for (const line of orderLines) {
      if (line.itemVariantId !== component.productSizeId) continue;

      const alreadyUsed = usedLineQuantities.get(line.itemVariantId + (line.commentId ?? '')) ?? 0;
      const availableFromLine = line.quantity - alreadyUsed;
      if (availableFromLine <= 0) continue;

      const qtyToDelete = Math.min(remainingQty, availableFromLine);
      usedLineQuantities.set(line.itemVariantId + (line.commentId ?? ''), alreadyUsed + qtyToDelete);

      deleteItems.push({
        item: {
          itemVariantId: line.itemVariantId,
          name: line.name,
          price: line.price,
          takeawayPrice: line.takeawayPrice,
          options: line.options.map((opt) => ({
            itemVariantId: opt.itemVariantId,
            name: opt.name,
            price: opt.price,
            takeawayPrice: opt.takeawayPrice,
            type: opt.type,
            quantity: opt.quantity,
          })),
          comment: line.comment,
          commentId: line.commentId,
          courseId: line.courseId,
          menuSectionId: line.menuSectionId,
          sectionProductId: line.sectionProductId,
          components: line.components?.map(mapCartComponentToAddComponent),
        },
        quantity: qtyToDelete,
      });

      remainingQty -= qtyToDelete;
      if (remainingQty <= 0) break;
    }
  }

  // Build customizations and comments maps from matched cart lines
  const componentCustomizations = new Map<string, CartAdditionOption[]>();
  const componentComments = new Map<string, { comment?: string; commentId?: string }>();

  for (const line of orderLines) {
    if (componentCustomizations.has(line.itemVariantId)) continue;

    const isMatchedComponent = match.bundle.components.some((c) => c.productSizeId === line.itemVariantId);
    if (!isMatchedComponent) continue;

    if (line.options.length > 0) {
      componentCustomizations.set(
        line.itemVariantId,
        line.options.map((opt) => ({
          type: opt.type as ProductType,
          option: {
            optionId: opt.itemVariantId,
            name: opt.name,
            price: opt.price,
            takeawayPrice: opt.takeawayPrice,
            preSelected: false,
          },
          quantity: opt.quantity,
          vatType: 'EAT_IN' as const,
        }))
      );
    }

    if (line.comment || line.commentId) {
      componentComments.set(line.itemVariantId, { comment: line.comment, commentId: line.commentId });
    }
  }

  // Build the composite add item using existing buildAddComponentDto
  const components = bundleProduct.components.map((c) => {
    const component = buildAddComponentDto(
      c,
      allProducts,
      componentCustomizations,
      bundleProduct.menuSectionId,
      bundleProduct.sectionProductId
    );
    const commentInfo = componentComments.get(c.productSizeId);
    if (commentInfo) {
      component.comment = commentInfo.comment;
      component.commentId = commentInfo.commentId;
    }
    return component;
  });

  const addBundle: AddItemsDto = {
    item: {
      itemVariantId: bundleProduct.variantLinkId,
      name: bundleProduct.name,
      price: bundleProduct.price,
      takeawayPrice: bundleProduct.takeawayPrice,
      options: [],
      courseId: bundleProduct.courseId,
      menuSectionId: bundleProduct.menuSectionId,
      sectionProductId: bundleProduct.sectionProductId,
      components,
    },
    quantity: match.instances,
  };

  return { deleteItems, addBundle };
}

const BundleSingleMatch: FC<{ match: BundleMatch; formatCurrency: (v: number) => string }> = ({
  match,
  formatCurrency,
}) => {
  const intl = useIntl();
  const instancesSuffix = match.instances > 1 ? ` x${match.instances}` : '';
  const savingsText = formatCurrency(match.bundle.savings * match.instances);

  return (
    <p>
      {intl.formatMessage({
        id: 'bundle.conversion.content',
        values: { name: match.bundle.name + instancesSuffix, savings: savingsText },
      })}
    </p>
  );
};

const BundleCandidateSelector: FC<{
  candidates: BundleMatch[];
  selectedIndex: number;
  onSelect: (index: number) => void;
  formatCurrency: (v: number) => string;
}> = ({ candidates, selectedIndex, onSelect, formatCurrency }) => {
  const intl = useIntl();

  return (
    <div>
      <p className="mb-2">{intl.formatMessage({ id: 'bundle.conversion.chooseBundle' })}</p>
      <div className="flex flex-col gap-2">
        {candidates.map((candidate, index) => {
          const isSelected = index === selectedIndex;
          const instancesSuffix = candidate.instances > 1 ? ` x${candidate.instances}` : '';
          const savingsText = formatCurrency(candidate.bundle.savings * candidate.instances);

          return (
            <button
              key={candidate.bundle.variantId}
              type="button"
              className={`flex items-center gap-3 rounded-lg border p-3 text-left transition-colors ${
                isSelected ? 'border-blue bg-blue-light' : 'border-grey-light hover:border-grey'
              }`}
              onClick={() => onSelect(index)}
            >
              <span
                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                  isSelected ? 'border-blue' : 'border-grey'
                }`}
              >
                {isSelected && <span className="h-2.5 w-2.5 rounded-full bg-blue" />}
              </span>
              <span className="flex flex-col">
                <span className="font-medium">
                  {candidate.bundle.name}
                  {instancesSuffix}
                </span>
                <span className="text-small text-grey-dark">
                  {intl.formatMessage({
                    id: 'bundle.conversion.savingsLabel',
                    values: { savings: savingsText },
                  })}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

function getTiedCandidates(matches: BundleMatch[]): BundleMatch[] {
  if (matches.length <= 1) return matches;
  const topComponentCount = matches[0]!.bundle.components.length;
  return matches.filter((m) => m.bundle.components.length === topComponentCount);
}

const BundleConversionDialog: FC = () => {
  const intl = useIntl();
  const { formatCurrency } = useLocation();
  const allProducts = useAllProducts();
  const [selectedIndex, setSelectedIndex] = useState(0);

  const { pendingBundlePrompt, orderLines, dismissBundlePrompt, convertToBundle } = useCartStore((state) => ({
    pendingBundlePrompt: state.pendingBundlePrompt,
    orderLines: state.cart.orderLines,
    dismissBundlePrompt: state.dismissBundlePrompt,
    convertToBundle: state.convertToBundle,
  }));

  const tiedCandidates = useMemo(() => getTiedCandidates(pendingBundlePrompt ?? []), [pendingBundlePrompt]);
  const selectedMatch = tiedCandidates[selectedIndex] ?? tiedCandidates[0];

  const payload = useMemo(() => {
    if (!selectedMatch) return undefined;
    return buildConversionPayload(selectedMatch, orderLines, allProducts);
  }, [selectedMatch, orderLines, allProducts]);

  const handleConfirm = useCallback(() => {
    if (!payload) return;
    convertToBundle(payload.deleteItems, payload.addBundle);
  }, [payload, convertToBundle]);

  if (!pendingBundlePrompt || pendingBundlePrompt.length === 0 || !selectedMatch) {
    return null;
  }

  const showSelector = tiedCandidates.length > 1;
  const remainingCount = pendingBundlePrompt.length - tiedCandidates.length;

  return (
    <Dialog
      className="w-full mx-4 tablet:w-1/2 tablet:mx-0"
      onClose={dismissBundlePrompt}
      title={intl.formatMessage({ id: 'bundle.conversion.title' })}
      onAction={handleConfirm}
      actionLabel={intl.formatMessage({ id: 'bundle.conversion.confirm' })}
      onSecondaryAction={dismissBundlePrompt}
      secondaryActionLabel={intl.formatMessage({ id: 'generic.cancel' })}
    >
      {showSelector ? (
        <BundleCandidateSelector
          candidates={tiedCandidates}
          selectedIndex={selectedIndex}
          onSelect={setSelectedIndex}
          formatCurrency={formatCurrency}
        />
      ) : (
        <BundleSingleMatch match={selectedMatch} formatCurrency={formatCurrency} />
      )}
      {remainingCount > 0 && (
        <p className="text-small text-grey-dark mt-2">
          {intl.formatMessage({
            id: 'bundle.conversion.otherOptions',
            values: { count: String(remainingCount) },
          })}
        </p>
      )}
    </Dialog>
  );
};

export default BundleConversionDialog;
